import { Icon } from "./Icon";
import { Menu } from "./Menu";

/* The stage path on lead and opportunity pages (UX-146): the open stages as arrow segments,
   earlier ones done (with a check), the current one highlighted, later ones muted, and the
   outcomes (Closed Won / Closed Lost, Qualified / Disqualified) as a final segment coloured won
   or lost. Clicking a segment does what the page decides (move, or open a dialog). Phones get
   "Stage 3 of 5: Proposal" above a path that scrolls sideways.

   An outcome can hold a `menu` (a bug's Closed: Resolved, Can't reproduce, Won't fix, ...): the
   segment is then one button that opens the options, and when the current value is one of them
   the segment shows it as "Closed: Won't fix". */

export interface PathStep {
  key: string;
  label: string;
}

export type PathTone =
  /** Finished well (green): every stage counts as done. */
  | "won"
  /** Finished without the stages being done (red). */
  | "lost"
  /** Open, off the path (amber): waiting, such as a bug's Need Information. */
  | "hold"
  /** Open, off the path (red): sent back, such as a bug whose fix failed its check. */
  | "back";

export interface PathMenuOption extends PathStep {
  /** The segment takes this tone while this option is the current value. */
  tone: PathTone;
}

export interface PathOutcome extends PathStep {
  tone: PathTone;
  /** Several options behind one segment, each its own value. The first is the main one. */
  menu?: PathMenuOption[];
}

type State = "done" | "current" | "todo";

/** The outcome that is the current value, directly or through its menu, with the tone and the
 *  text its segment shows ("Closed: Won't fix" for an option of a menu). */
function currentOutcome(outcomes: PathOutcome[], current: string) {
  for (const o of outcomes) {
    if (o.key === current && !o.menu?.some((m) => m.key === current)) {
      return { outcome: o, tone: o.tone, text: o.label };
    }
    const option = o.menu?.find((m) => m.key === current);
    if (option) return { outcome: o, tone: option.tone, text: `${o.label}: ${option.label}` };
  }
  return undefined;
}

export function stepState(steps: PathStep[], outcomes: PathOutcome[], current: string, key: string): State {
  if (key === current) return "current";
  const here = currentOutcome(outcomes, current);
  if (here && here.outcome.key === key) return "current"; // the segment whose menu holds it
  const at = steps.findIndex((s) => s.key === current);
  const index = steps.findIndex((s) => s.key === key);
  if (index < 0) return "todo"; // an outcome (or on-hold state) that isn't the current one
  if (at >= 0) return index < at ? "done" : "todo";
  // Finished: a win completed every stage; a loss leaves them muted. On hold or sent back (open
  // states off the path, such as a bug's Need Information or Not Fixed) leave the stages upcoming.
  return here?.tone === "won" ? "done" : "todo";
}

const SAYS: Record<State, string> = { done: "completed", current: "current stage", todo: "upcoming" };

/** What the summary line (phones) calls a tone: "Outcome: Closed Won", "On hold: Waiting". */
const SUMMARY: Record<PathTone, string> = {
  won: "Outcome",
  lost: "Outcome",
  hold: "On hold",
  back: "Sent back",
};

/** What assistive tech hears after an outcome's name when it isn't the current value. */
const ROLE: Record<PathTone, string> = {
  won: "final outcome",
  lost: "final outcome",
  hold: "on hold",
  back: "sent back",
};

const ICON = { won: "check", lost: "x", hold: "clock", back: "x" } as const;

export function StagePath({
  label,
  steps,
  outcomes,
  current,
  onSelect,
  disabled,
  readOnly,
}: {
  /** Names the path for screen readers, e.g. "Opportunity stage". */
  label: string;
  steps: PathStep[];
  outcomes: PathOutcome[];
  current: string;
  onSelect?: (key: string) => void;
  /** Whether a segment (or an option of a menu) can't be chosen now. */
  disabled?: (key: string) => boolean;
  /** Show the status only (the client portal): no segment can be chosen. */
  readOnly?: boolean;
}) {
  const off = (key: string) => !!readOnly || !!disabled?.(key);
  const at = steps.findIndex((s) => s.key === current);
  const here = currentOutcome(outcomes, current);
  const summary =
    at >= 0
      ? `Stage ${at + 1} of ${steps.length}: ${steps[at]!.label}`
      : here
        ? `${SUMMARY[here.tone]}: ${here.text}`
        : "";
  return (
    <nav className={readOnly ? "spath spath-ro" : "spath"} aria-label={label}>
      {summary && (
        <p className="spath-summary" aria-hidden="true">
          {summary}
        </p>
      )}
      <ol className="spath-list">
        {steps.map((step) => {
          const state = stepState(steps, outcomes, current, step.key);
          return (
            <li key={step.key} className={`spath-seg ${state}`}>
              <button
                type="button"
                aria-label={`${step.label}, ${SAYS[state]}`}
                aria-current={state === "current" ? "step" : undefined}
                disabled={off(step.key)}
                onClick={() => step.key !== current && onSelect?.(step.key)}
              >
                {state === "done" && <Icon name="check" />}
                <span>{step.label}</span>
              </button>
            </li>
          );
        })}
        <li className="spath-end" aria-hidden="true" />
        {outcomes.map((o) => {
          const mine = here?.outcome === o ? here : undefined;
          const isHere = !!mine;
          const tone = mine?.tone ?? o.tone;
          const text = mine?.text ?? o.label;
          const name = `${text}, ${isHere ? (tone === "hold" || tone === "back" ? `${ROLE[tone]}, current stage` : "current stage") : ROLE[tone]}`;
          const icon = isHere ? <Icon name={ICON[tone]} /> : null;
          return (
            <li key={o.key} className={`spath-out ${tone}${isHere ? " current" : ""}`}>
              {o.menu ? (
                <Menu
                  label={`${o.label} options`}
                  triggerLabel={name}
                  triggerClassName="spath-menu"
                  triggerCurrent={isHere}
                  disabled={o.menu.every((m) => off(m.key) || m.key === current)}
                  trigger={
                    <>
                      {icon}
                      <span>{text}</span>
                      <Icon name="down" />
                    </>
                  }
                  items={o.menu.map((m) => ({
                    label: m.label,
                    current: m.key === current,
                    disabled: off(m.key),
                    onSelect: () => m.key !== current && onSelect?.(m.key),
                  }))}
                />
              ) : (
                <button
                  type="button"
                  aria-label={name}
                  aria-current={isHere ? "step" : undefined}
                  disabled={off(o.key)}
                  onClick={() => !isHere && onSelect?.(o.key)}
                >
                  {icon}
                  <span>{text}</span>
                </button>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
